(() => {
  const initialize = (root) => {
    if (!root || root.dataset.lgReady === 'true') return;
    root.dataset.lgReady = 'true';

    const cleanup = [];
    const listen = (target, eventName, handler, options) => {
      if (!target) return;
      target.addEventListener(eventName, handler, options);
      cleanup.push(() => target.removeEventListener(eventName, handler, options));
    };

    const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
    const recipientButtons = [...root.querySelectorAll('[data-lg-recipient]')];
    const occasionSelect = root.querySelector('[data-lg-occasion]');
    const occasionControl = root.querySelector('[data-lg-occasion-control]');
    const occasionTrigger = root.querySelector('[data-lg-occasion-trigger]');
    const occasionLabel = root.querySelector('[data-lg-occasion-label]');
    const occasionOptions = root.querySelector('[data-lg-occasion-options]');
    const occasionOptionButtons = [...root.querySelectorAll('[data-lg-occasion-option]')];
    const productGrid = root.querySelector('[data-lg-product-grid]');
    const emptyState = root.querySelector('[data-lg-empty]');
    const loadingState = root.querySelector('[data-lg-loading]');
    let productItems = [...root.querySelectorAll('[data-lg-product]')];
    let selectedRecipient = 'all';
    let productsAreLoading = Boolean(root.querySelector('[data-lg-next-page]'));

    const normalize = (value) => String(value || '').trim().toLowerCase();
    const valuesFrom = (value) =>
      normalize(value)
        .split('|')
        .map((item) => item.trim())
        .filter(Boolean);
    const aliasesFrom = (value) =>
      normalize(value)
        .split(',')
        .map((item) => item.trim())
        .filter(Boolean);
    const baseValue = (value) =>
      normalize(value)
        .replace(/\s*[\(（].*$/, '')
        .trim();
    const matchesValue = (source, value) => {
      const selectedValues = aliasesFrom(value);
      if (!selectedValues.length || selectedValues.includes('all')) return true;

      return valuesFrom(source).some((sourceValue) => {
        const sourceBaseValue = baseValue(sourceValue);
        const sourceSuffix = sourceValue.split(':').pop().trim();
        const suffixBaseValue = baseValue(sourceSuffix);
        return selectedValues.some(
          (selectedValue) =>
            sourceValue === selectedValue ||
            sourceBaseValue === selectedValue ||
            sourceSuffix === selectedValue ||
            suffixBaseValue === selectedValue,
        );
      });
    };

    const filterProducts = () => {
      const selectedOccasion = normalize(occasionSelect?.value || 'all');
      let visibleCount = 0;

      productItems.forEach((item) => {
        const recipientData = item.dataset.lgRecipients || item.dataset.lgTags || '';
        const occasionData = item.dataset.lgOccasions || item.dataset.lgTags || '';
        const recipientMatches = matchesValue(recipientData, selectedRecipient);
        const occasionMatches = matchesValue(occasionData, selectedOccasion);
        const shouldShow = recipientMatches && occasionMatches;

        item.hidden = !shouldShow;
        if (shouldShow) visibleCount += 1;
      });

      if (emptyState) emptyState.hidden = productsAreLoading || visibleCount > 0;
    };

    recipientButtons.forEach((button) => {
      listen(button, 'click', () => {
        selectedRecipient = normalize(button.dataset.lgRecipient) || 'all';
        recipientButtons.forEach((candidate) => {
          const isActive = candidate === button;
          candidate.classList.toggle('is-active', isActive);
          candidate.setAttribute('aria-pressed', String(isActive));
        });
        filterProducts();
      });
    });

    const closeOccasionOptions = () => {
      if (!occasionTrigger || !occasionOptions) return;
      occasionTrigger.setAttribute('aria-expanded', 'false');
      occasionOptions.hidden = true;
    };

    const openOccasionOptions = (focusSelected = false) => {
      if (!occasionTrigger || !occasionOptions) return;
      occasionTrigger.setAttribute('aria-expanded', 'true');
      occasionOptions.hidden = false;

      if (focusSelected) {
        const selected = occasionOptionButtons.find((option) => option.classList.contains('is-selected')) || occasionOptionButtons[0];
        selected?.focus();
      }
    };

    const selectOccasion = (value, label) => {
      const normalizedValue = normalize(value) || 'all';
      if (occasionSelect) occasionSelect.value = normalizedValue;
      if (occasionLabel) occasionLabel.textContent = label || occasionSelect?.selectedOptions?.[0]?.textContent || '';

      occasionOptionButtons.forEach((option) => {
        const isSelected = normalize(option.dataset.lgOccasionOption) === normalizedValue;
        option.classList.toggle('is-selected', isSelected);
        option.setAttribute('aria-selected', String(isSelected));
      });

      closeOccasionOptions();
      filterProducts();
    };

    listen(occasionTrigger, 'click', () => {
      if (occasionTrigger.getAttribute('aria-expanded') === 'true') closeOccasionOptions();
      else openOccasionOptions();
    });

    listen(occasionTrigger, 'keydown', (event) => {
      if (event.key === 'Escape') {
        closeOccasionOptions();
        return;
      }
      if (!['ArrowDown', 'ArrowUp', 'Enter', ' '].includes(event.key)) return;
      event.preventDefault();
      openOccasionOptions(true);
    });

    occasionOptionButtons.forEach((option, index) => {
      listen(option, 'click', () => {
        selectOccasion(option.dataset.lgOccasionOption, option.textContent.trim());
        occasionTrigger?.focus();
      });

      listen(option, 'keydown', (event) => {
        let nextIndex = index;
        if (event.key === 'ArrowDown') nextIndex = (index + 1) % occasionOptionButtons.length;
        else if (event.key === 'ArrowUp') nextIndex = (index - 1 + occasionOptionButtons.length) % occasionOptionButtons.length;
        else if (event.key === 'Home') nextIndex = 0;
        else if (event.key === 'End') nextIndex = occasionOptionButtons.length - 1;
        else if (event.key === 'Escape') {
          event.preventDefault();
          closeOccasionOptions();
          occasionTrigger?.focus();
          return;
        } else if (event.key === 'Enter' || event.key === ' ') {
          event.preventDefault();
          selectOccasion(option.dataset.lgOccasionOption, option.textContent.trim());
          occasionTrigger?.focus();
          return;
        } else return;

        event.preventDefault();
        occasionOptionButtons[nextIndex]?.focus();
      });
    });

    listen(occasionSelect, 'change', () => {
      const selectedOption = occasionOptionButtons.find((option) => normalize(option.dataset.lgOccasionOption) === normalize(occasionSelect.value));
      selectOccasion(occasionSelect.value, selectedOption?.textContent.trim() || occasionSelect.selectedOptions?.[0]?.textContent || '');
    });

    listen(document, 'click', (event) => {
      if (occasionControl && !occasionControl.contains(event.target)) closeOccasionOptions();
    });

    const loadRemainingProducts = async () => {
      if (!productGrid) return;
      let nextUrl = root.querySelector('[data-lg-next-page]')?.dataset.lgNextPage;

      try {
        while (nextUrl) {
          const url = new URL(nextUrl, window.location.origin);
          url.searchParams.set('section_id', root.dataset.lgRoot);
          const response = await fetch(url.toString(), { headers: { 'X-Requested-With': 'XMLHttpRequest' } });
          if (!response.ok) throw new Error(`Unable to load gift products (${response.status})`);

          const parsed = new DOMParser().parseFromString(await response.text(), 'text/html');
          const nextRoot = parsed.querySelector('[data-lg-root]');
          if (!nextRoot) break;

          const newItems = [...nextRoot.querySelectorAll('[data-lg-product]')];
          newItems.forEach((item) => productGrid.append(item));
          productItems = [...root.querySelectorAll('[data-lg-product]')];
          nextUrl = nextRoot.querySelector('[data-lg-next-page]')?.dataset.lgNextPage || '';
        }
      } catch (error) {
        console.warn('[Lotus Gifts] Complete collection loading stopped.', error);
      } finally {
        productsAreLoading = false;
        if (loadingState) loadingState.hidden = true;
        filterProducts();
      }
    };

    const revealItems = [...root.querySelectorAll('[data-lg-reveal]')];
    if (!reduceMotion.matches && 'IntersectionObserver' in window && revealItems.length) {
      root.classList.add('lg-motion-ready');
      revealItems.forEach((item, index) => item.style.setProperty('--lg-reveal-delay', `${Math.min(index % 4, 3) * 70}ms`));

      const observer = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          entry.target.classList.add('is-visible');
          observer.unobserve(entry.target);
        });
      }, { rootMargin: '0px 0px -8% 0px', threshold: 0.08 });

      revealItems.forEach((item) => observer.observe(item));
      cleanup.push(() => observer.disconnect());
    } else {
      revealItems.forEach((item) => item.classList.add('is-visible'));
    }

    selectOccasion(occasionSelect?.value || 'all', occasionOptionButtons[0]?.textContent.trim());
    loadRemainingProducts();

    root.lgDestroy = () => {
      cleanup.forEach((callback) => callback());
      delete root.lgDestroy;
      delete root.dataset.lgReady;
    };
  };

  const initializeAll = (scope = document) => {
    if (scope.matches?.('[data-lg-root]')) initialize(scope);
    scope.querySelectorAll?.('[data-lg-root]').forEach(initialize);
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initializeAll(), { once: true });
  } else {
    initializeAll();
  }

  document.addEventListener('shopify:section:load', (event) => initializeAll(event.target));
  document.addEventListener('shopify:section:unload', (event) => {
    event.target.querySelectorAll?.('[data-lg-root]').forEach((root) => root.lgDestroy?.());
  });
})();
